import api from "@/app/services/api";
import nirApi from "@/app/services/apiNir";
import type { EntityType } from "@/domains/home/services/nir/configurations/types/forms";

export type TypeMappingEntry = {
  id: string;
  name: string;
  parentId: string | null;
  parentName: string | null;
};

export type TypeMappingsData = {
  types: Record<string, TypeMappingEntry | null>;
  subTypes: Record<string, TypeMappingEntry | null>;
};

export type TypeMappingsPayload = {
  types: Record<string, TypeMappingEntry>;
  subTypes: Record<string, TypeMappingEntry>;
};

export type EntityTypeOption = {
  id: string;
  name: string;
  parentId: string | null;
  parentName: string | null;
  label: string;
};

export function extractErrorMessage(error: unknown, fallback: string) {
  const err = error as { response?: { data?: { message?: string } }; message?: string };
  return err.response?.data?.message || err.message || fallback;
}

export function formatSlugLabel(value: string) {
  return value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function unwrapEntityTypes(body: unknown): EntityType[] {
  const root = body as { data?: { data?: EntityType[] } | EntityType[] };
  if (root?.data != null && typeof root.data === "object" && !Array.isArray(root.data)) {
    const nested = root.data as { data?: EntityType[] };
    if (Array.isArray(nested.data)) return nested.data;
  }
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(body)) return body as EntityType[];
  return [];
}

function toMappingEntry(entity: EntityTypeOption): TypeMappingEntry {
  return {
    id: entity.id,
    name: entity.name,
    parentId: entity.parentId,
    parentName: entity.parentName,
  };
}

function buildMappingData(
  typeKeys: string[],
  subTypeKeys: string[],
  typeSelections: Record<string, string>,
  subTypePrimarySelections: Record<string, string>,
  subTypeSelections: Record<string, string>,
  primaryOptions: EntityTypeOption[],
  subOptionsByParentId: Record<string, EntityTypeOption[]>
): TypeMappingsData {
  const types: Record<string, TypeMappingEntry | null> = {};
  const subTypes: Record<string, TypeMappingEntry | null> = {};

  for (const key of typeKeys) {
    const selectedId = typeSelections[key];
    const match = primaryOptions.find((option) => option.id === selectedId);
    types[key] = match ? toMappingEntry(match) : null;
  }

  for (const key of subTypeKeys) {
    const selectedId = subTypeSelections[key];
    const parentId = subTypePrimarySelections[key];
    const parent = primaryOptions.find((option) => option.id === parentId);
    const subOptions = parentId ? subOptionsByParentId[parentId] ?? [] : [];
    const match = subOptions.find((option) => option.id === selectedId);

    if (match && parent) {
      subTypes[key] = {
        id: match.id,
        name: match.name,
        parentId: parent.id,
        parentName: parent.name,
      };
    } else {
      subTypes[key] = null;
    }
  }

  return { types, subTypes };
}

function pickSelectedEntries(
  entries: Record<string, TypeMappingEntry | null>
): Record<string, TypeMappingEntry> {
  const selected: Record<string, TypeMappingEntry> = {};
  for (const [key, value] of Object.entries(entries)) {
    if (value?.id) selected[key] = value;
  }
  return selected;
}

export function buildSelectedMappingsPayload(
  typeKeys: string[],
  subTypeKeys: string[],
  typeSelections: Record<string, string>,
  subTypePrimarySelections: Record<string, string>,
  subTypeSelections: Record<string, string>,
  primaryOptions: EntityTypeOption[],
  subOptionsByParentId: Record<string, EntityTypeOption[]>
): TypeMappingsPayload {
  const data = buildMappingData(
    typeKeys,
    subTypeKeys,
    typeSelections,
    subTypePrimarySelections,
    subTypeSelections,
    primaryOptions,
    subOptionsByParentId
  );

  return {
    types: pickSelectedEntries(data.types),
    subTypes: pickSelectedEntries(data.subTypes),
  };
}

export function buildSingleTypeMappingPayload(
  key: string,
  typeKeys: string[],
  subTypeKeys: string[],
  typeSelections: Record<string, string>,
  subTypePrimarySelections: Record<string, string>,
  subTypeSelections: Record<string, string>,
  primaryOptions: EntityTypeOption[],
  subOptionsByParentId: Record<string, EntityTypeOption[]>
): TypeMappingsPayload {
  const data = buildMappingData(
    typeKeys,
    subTypeKeys,
    typeSelections,
    subTypePrimarySelections,
    subTypeSelections,
    primaryOptions,
    subOptionsByParentId
  );
  const entry = data.types[key];

  return {
    types: entry?.id ? { [key]: entry } : {},
    subTypes: {},
  };
}

export function buildSingleSubTypeMappingPayload(
  key: string,
  typeKeys: string[],
  subTypeKeys: string[],
  typeSelections: Record<string, string>,
  subTypePrimarySelections: Record<string, string>,
  subTypeSelections: Record<string, string>,
  primaryOptions: EntityTypeOption[],
  subOptionsByParentId: Record<string, EntityTypeOption[]>
): TypeMappingsPayload {
  const data = buildMappingData(
    typeKeys,
    subTypeKeys,
    typeSelections,
    subTypePrimarySelections,
    subTypeSelections,
    primaryOptions,
    subOptionsByParentId
  );
  const entry = data.subTypes[key];

  return {
    types: {},
    subTypes: entry?.id ? { [key]: entry } : {},
  };
}

export async function fetchPrimaryEntityTypeOptions(): Promise<EntityTypeOption[]> {
  const response = await nirApi.get("/v1/configurations/entity-types/primary");
  return unwrapEntityTypes(response.data).map((entity) => ({
    id: entity.id,
    name: entity.name,
    parentId: entity.parentId || null,
    parentName: null,
    label: entity.name,
  }));
}

export async function fetchEntityTypesByParentId(
  parentId: string,
  primaryOptions: EntityTypeOption[]
): Promise<EntityTypeOption[]> {
  const parent = primaryOptions.find((option) => option.id === parentId);
  const response = await nirApi.get(`/v1/configurations/entity-types?parentId=${parentId}`);
  return unwrapEntityTypes(response.data).map((entity) => ({
    id: entity.id,
    name: entity.name,
    parentId: entity.parentId || parentId,
    parentName: parent?.name ?? null,
    label: entity.name,
  }));
}

export async function fetchTypeMappings(): Promise<TypeMappingsData> {
  const response = await api.get("/integration/api/nir/type-mappings");
  const data = (response.data?.data ?? response.data) as TypeMappingsData | undefined;

  return {
    types: data?.types ?? {},
    subTypes: data?.subTypes ?? {},
  };
}

export async function saveTypeMappings(payload: TypeMappingsPayload) {
  const response = await api.post("/integration/api/nir/type-mappings", payload);
  const body = response.data as {
    status?: number;
    message?: string;
    error?: unknown;
    data?: unknown;
  };

  if (body?.status != null && body.status >= 400) {
    throw new Error(body.message || "Failed to save type mappings");
  }

  return response;
}

export function selectionsFromMappings(
  mappings: Record<string, TypeMappingEntry | null>
): Record<string, string> {
  const selections: Record<string, string> = {};
  for (const [key, value] of Object.entries(mappings)) {
    selections[key] = value?.id ?? "";
  }
  return selections;
}

export function primarySelectionsFromSubMappings(
  mappings: Record<string, TypeMappingEntry | null>
): Record<string, string> {
  const selections: Record<string, string> = {};
  for (const [key, value] of Object.entries(mappings)) {
    selections[key] = value?.parentId ?? "";
  }
  return selections;
}
